import { useState } from 'react'
import { CheckCircle2, ArrowRight } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { Spinner } from '../../lib/shadcn/spinner'
import { Chip } from '../interviews/Chip'
import { formatDate } from '../../utils/interviews'
import { materialWarning, type BatchPreview } from './batch'
import type { PendingCorrection } from './reconcile'

// The mandatory confirm step before a batch is approved. The action bar never
// approves directly — it opens this dialog, which lists every selected
// correction compactly (date, from -> to, minor/material) so the reviewer sees
// exactly what they are signing off, not just a count. If any material rows are
// selected the exact amber warning is repeated here.
export function BatchApproveDialog({
  open,
  onOpenChange,
  rows,
  preview,
  loading,
  error,
  onConfirm,
}: {
  open: boolean
  onOpenChange: (next: boolean) => void
  rows: PendingCorrection[]
  preview: BatchPreview | null
  loading: boolean
  error: string | null
  onConfirm: (notes: string | null) => void
}) {
  const [notes, setNotes] = useState('')

  const materialCount = preview?.material_count ?? rows.filter((r) => r.correction_type === 'material').length

  function handleConfirm() {
    const trimmed = notes.trim()
    onConfirm(trimmed.length > 0 ? trimmed : null)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Approve {rows.length} correction{rows.length === 1 ? '' : 's'}
          </DialogTitle>
          <DialogDescription>
            Each correction is applied in its own subtransaction. A refusal on one row does not roll
            back the others — you will see the outcome of every row afterwards.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {materialCount > 0 && preview ? (
            <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100">
              {materialWarning(preview.owners_affected, preview.owner_money_moved_zar)}
            </p>
          ) : null}

          <div className="max-h-64 overflow-y-auto rounded-md border border-border">
            <ul className="divide-y divide-border">
              {rows.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
                  <span className="w-24 shrink-0 font-medium text-foreground">
                    {formatDate(r.shift_date)}
                  </span>
                  <span className="flex flex-1 items-center gap-1.5 font-mono text-xs text-foreground">
                    <span>{r.from_value || '—'}</span>
                    <ArrowRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span>{r.to_value || '—'}</span>
                  </span>
                  <Chip tone={r.correction_type === 'material' ? 'red' : 'neutral'}>
                    {r.correction_type}
                  </Chip>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="batch-notes" className="text-xs font-medium text-muted-foreground">
              Notes (optional, applied to all)
            </Label>
            <Textarea
              id="batch-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional note recorded against every approval in this batch."
              rows={2}
              className="text-sm"
            />
          </div>

          {error ? (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 p-2.5 text-sm text-destructive">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={loading || rows.length === 0}>
            {loading ? (
              <Spinner className="mr-1.5 h-4 w-4" />
            ) : (
              <CheckCircle2 className="mr-1.5 h-4 w-4" />
            )}
            Approve {rows.length} correction{rows.length === 1 ? '' : 's'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
